import { useState } from 'react'

import { ChargeRecurrenceBodyModalForm } from 'templates/Charge/interface'

/**
 * SC-10920: o assistente não executa escrita, mas prepara o formulário.
 *
 * Quando o merchant pede "cria uma cobrança pra ele", o agente devolve no fim
 * da resposta um bloco [[UI_ACTION]]{...}[[/UI_ACTION]] com o que a conversa já
 * apurou (cliente, valor, vencimento). O chat lê esse bloco, remove do texto e
 * abre o modal de sempre com os campos preenchidos.
 *
 * Quem confere e salva continua sendo o merchant, no formulário que ele já
 * conhece e com as validações de sempre.
 */
export type ChatUiActionType =
  | 'open_charge_modal'
  | 'open_recurrence_modal'
  | 'open_customer_modal'

export interface ChatUiAction {
  type: ChatUiActionType
  customer_uuid?: string
  customer_name?: string
  defaults?: {
    amount?: number | string
    due_date?: string
    description?: string
  }
}

const UI_ACTION_REGEX = /\[\[UI_ACTION\]\]([\s\S]*?)\[\[\/UI_ACTION\]\]/

const TYPES: ChatUiActionType[] = [
  'open_charge_modal',
  'open_recurrence_modal',
  'open_customer_modal'
]

/**
 * Separa o texto visível da ação. O bloco é gerado por um LLM, então tudo aqui
 * é defensivo: JSON inválido ou tipo desconhecido vira "sem ação" e o usuário
 * ao menos continua vendo a resposta em texto, em vez de um erro.
 */
export const parseUiAction = (
  raw: string
): { text: string; action: ChatUiAction | null } => {
  if (!raw || typeof raw !== 'string') return { text: raw || '', action: null }

  const match = raw.match(UI_ACTION_REGEX)
  if (!match) return { text: raw, action: null }

  const text = raw.replace(UI_ACTION_REGEX, '').trim()

  try {
    const parsed = JSON.parse(match[1].trim())
    if (!TYPES.includes(parsed?.type)) return { text, action: null }

    // Cobrança e recorrência são sempre de um cliente: sem o uuid o modal
    // abriria com o campo vazio e o merchant teria que buscar de novo, que é
    // exatamente a fricção que esta feature remove.
    if (parsed.type !== 'open_customer_modal' && !parsed.customer_uuid) {
      return { text, action: null }
    }

    return { text, action: parsed as ChatUiAction }
  } catch {
    return { text, action: null }
  }
}

/** Converte a ação do agente nos campos que o formulário espera. */
export const toFormDefaults = (
  action: ChatUiAction
): Partial<ChargeRecurrenceBodyModalForm> => {
  const defaults: Partial<ChargeRecurrenceBodyModalForm> = {}
  const { customer_uuid: uuid, customer_name: name } = action

  if (uuid) {
    defaults.customerUuid = uuid
    defaults.recieverType = 'customer'
    if (name) defaults.customer = { label: name, value: uuid } as never
  }

  const amount = action.defaults?.amount
  if (amount !== undefined && amount !== null && amount !== '') {
    defaults.amountBilled = amount
  }

  const description = action.defaults?.description
  if (description) defaults.description = description

  // A data chega como AAAA-MM-DD; o formulário trabalha com Date. Interpretar
  // com o construtor de partes evita o deslocamento de fuso que `new Date(iso)`
  // provoca (vira o dia anterior no Brasil).
  const dueDate = action.defaults?.due_date
  if (dueDate && /^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
    const [year, month, day] = dueDate.split('-').map(Number)
    defaults.dueDate = new Date(year, month - 1, day)
  }

  return defaults
}

export const useChatUiAction = () => {
  const [action, setAction] = useState<ChatUiAction | null>(null)

  return {
    action,
    openFromAction: setAction,
    close: () => setAction(null),
    formDefaults: action ? toFormDefaults(action) : undefined
  }
}
